import { useState, useEffect, useCallback } from "react";
import { useRouter, useRouterState } from "@tanstack/react-router";
import type { DialogProps } from "@radix-ui/react-dialog";
import {
  CalendarCheck,
  LogOut,
  Moon,
  SquareAsterisk,
  SquareUserRound,
  Sun,
  UserCheck,
  UserRound,
  Home,
  Library,
  Sparkles,
  MessageCircle,
  CreditCard,
  Star,
  Calendar,
  CalendarCog,
  Bell,
} from "lucide-react";
import { useTheme } from "@/components/themeProvider";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { PATHS } from "@/config/paths";
import { contactDialog, forgotPasswordDialog, loginDialog, registerDialog } from "@/state/dialogs";
import { proDialog } from "@/state/dialogs";
import { useSetAtom } from "jotai";
import { useLogoutInvalidate } from "@/hooks/useLogoutInvalidate";
import { useUser } from "@/hooks/useUser";
import { useRoutines } from "@/hooks/routines/useRoutines";
import { useGoals } from "@/hooks/goals/useGoals";
import { CommonIcon } from "@/icons/CommonIcon";
import { ProgressCircle } from "@/components/ui/progress-circle";
import { getBrightRoutineColor, hasDayCompleted } from "@/hooks/routines/useRoutine.utils";
import type { Routine } from "instantdb/types";
import {
  LINKEDIN_URL,
  WHATSAPP_COMMUNITY_URL,
  X_URL,
  INSTAGRAM_URL,
  APP_STORE_URL,
  TIKTOK_URL,
} from "@/config/constants";
import {
  LinkedInIcon,
  XIcon,
  WhatsAppIcon,
  InstagramIcon,
  TikTokIcon,
} from "@/components/icons/Icons";
import { getRoutineCompletionPercentage } from "@/hooks/routines/useRoutine.utils";
import { useHealthAreas } from "@/hooks/healthAreas/useHealthAreas";
import { useTranslation } from "@/hooks/useTranslation";

type CommandMenuProps = {
  showButton?: boolean;
  className?: string;
} & DialogProps;

function CommandMenuRoutineItem({
  routine,
  runCommand,
}: {
  routine: Routine;
  runCommand: (fn: () => void) => void;
}) {
  const router = useRouter();
  const today = new Date().toISOString().split("T")[0];
  const isDayCompleted = hasDayCompleted(routine, today);

  const brightRoutineColor = getBrightRoutineColor(routine);
  const completionPercentage = getRoutineCompletionPercentage(routine, today);

  return (
    <CommandItem
      onSelect={() => runCommand(() => router.navigate({ to: PATHS.ROUTINES }))}
      className={cn("flex items-center gap-2", isDayCompleted && "opacity-75")}
    >
      {brightRoutineColor ? (
        <ProgressCircle
          progress={completionPercentage}
          className="shrink-0 size-5"
          color={brightRoutineColor}
        />
      ) : (
        <CommonIcon name="routines" className="size-4" />
      )}
      <span className={cn(isDayCompleted && "line-through")}>{routine.title}</span>
    </CommandItem>
  );
}

export function CommandMenu({ showButton = true, className, ...props }: CommandMenuProps) {
  const { goals } = useGoals();
  const { routines } = useRoutines();
  const { handleCreateHealthArea } = useHealthAreas();
  const { handleCreateGoal } = useGoals();
  const { handleCreateRoutine } = useRoutines();

  const { logout } = useLogoutInvalidate();
  const router = useRouter();
  const { isAuthenticated, hasPro } = useUser();
  const [open, setOpen] = useState(false);
  const { setTheme } = useTheme();
  const { t } = useTranslation();

  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const insideDashboard = pathname.includes(PATHS.DASHBOARD);

  const setLoginDialogOpen = useSetAtom(loginDialog);
  const setRegisterDialogOpen = useSetAtom(registerDialog);
  const setForgotPasswordDialogOpen = useSetAtom(forgotPasswordDialog);
  const setContactDialogOpen = useSetAtom(contactDialog);
  const setProDialogOpen = useSetAtom(proDialog);

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if ((e.key === "k" && (e.metaKey || e.ctrlKey)) || e.key === "/") {
        if (
          (e.target instanceof HTMLElement && e.target.isContentEditable) ||
          e.target instanceof HTMLInputElement ||
          e.target instanceof HTMLTextAreaElement ||
          e.target instanceof HTMLSelectElement
        ) {
          return;
        }

        e.preventDefault();
        setOpen((open) => !open);
      }
    };

    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  const runCommand = useCallback((command: () => unknown) => {
    setOpen(false);
    command();
  }, []);

  return (
    <>
      {showButton && (
        <Button
          aria-label={t("command_menu.open_command_menu")}
          variant="outline"
          className={cn(
            "relative h-8 w-full justify-start rounded-[0.5rem] bg-muted/50 text-sm font-normal text-muted-foreground shadow-none sm:pr-12 md:w-40 lg:w-56 xl:w-64",
            className,
          )}
          onClick={() => setOpen(true)}
          {...props}
        >
          <span>{t("command_menu.search_placeholder")}</span>
          <kbd className="pointer-events-none absolute right-[0.3rem] top-[0.3rem] hidden h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium opacity-100 sm:flex">
            <span className="text-xs">⌘</span>K
          </kbd>
        </Button>
      )}
      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput placeholder={t("command_menu.search_input_placeholder")} />
        <CommandList>
          <CommandEmpty>{t("command_menu.no_results")}</CommandEmpty>
          <CommandGroup heading={t("command_menu.links")}>
            <CommandItem onSelect={() => runCommand(() => router.navigate({ to: PATHS.HOME }))}>
              <Home />
              {t("nav.home")}
            </CommandItem>
            <CommandItem
              onSelect={() => runCommand(() => router.navigate({ to: PATHS.DASHBOARD }))}
            >
              <CalendarCheck />
              {t("sidebar.today")}
            </CommandItem>
            <CommandItem onSelect={() => runCommand(() => router.navigate({ to: PATHS.GOALS }))}>
              <CommonIcon name="goals" />
              {t("sidebar.goals")}
            </CommandItem>
            <CommandItem onSelect={() => runCommand(() => router.navigate({ to: PATHS.ROUTINES }))}>
              <CommonIcon name="routines" />
              {t("sidebar.routines")}
            </CommandItem>
            <CommandItem onSelect={() => runCommand(() => router.navigate({ to: PATHS.CALENDAR }))}>
              <Calendar />
              {t("sidebar.calendar")}
            </CommandItem>
            <CommandItem onSelect={() => runCommand(() => router.navigate({ to: PATHS.LIBRARY }))}>
              <Library />
              {t("sidebar.library")}
            </CommandItem>
            <CommandItem onSelect={() => runCommand(() => router.navigate({ to: PATHS.PRICING }))}>
              <CreditCard />
              {t("nav.pricing")}
            </CommandItem>

            {(!hasPro || !isAuthenticated) && (
              <CommandItem onSelect={() => runCommand(() => setProDialogOpen(true))}>
                <Sparkles />
                {t("command_menu.progressmade_health_pro")}
              </CommandItem>
            )}

            <CommandItem onSelect={() => runCommand(() => setContactDialogOpen(true))}>
              <MessageCircle />
              {t("command_menu.get_help_or_share_contact")}
            </CommandItem>

            <CommandItem onSelect={() => runCommand(() => window.open(APP_STORE_URL, "_blank"))}>
              <Star />
              {t("command_menu.rate_us")}
              <div className="flex gap-0.5 ml-auto">
                <Star className="size-4 fill-amber-400 stroke-amber-400" />
                <Star className="size-4 fill-amber-400 stroke-amber-400" />
                <Star className="size-4 fill-amber-400 stroke-amber-400" />
                <Star className="size-4 fill-amber-400 stroke-amber-400" />
                <Star className="size-4 fill-amber-400 stroke-amber-400" />
              </div>
            </CommandItem>
          </CommandGroup>
          <CommandSeparator />

          <CommandGroup heading={t("sidebar.goals")}>
            {goals.map((goal) => (
              <CommandItem
                key={goal.id}
                onSelect={() =>
                  runCommand(() => router.navigate({ to: `/dashboard/goal/${goal.id}` }))
                }
                className="flex items-center gap-2"
              >
                <span className="inline-flex items-center justify-center size-5 text-[20px] leading-none">
                  {goal.symbol}
                </span>
                {goal.title}
              </CommandItem>
            ))}
          </CommandGroup>
          <CommandSeparator />
          <CommandGroup heading={t("sidebar.routines")}>
            {routines.map((routine) => (
              <CommandMenuRoutineItem key={routine.id} routine={routine} runCommand={runCommand} />
            ))}
          </CommandGroup>
          <CommandSeparator />

          {insideDashboard && (
            <CommandGroup heading={t("command_menu.commands")}>
              <CommandItem onSelect={() => runCommand(() => handleCreateHealthArea({}))}>
                <CommonIcon name="healthAreas" />
                {t("sidebar.add_health_area")}
              </CommandItem>
              <CommandItem onSelect={() => runCommand(() => handleCreateGoal({}))}>
                <CommonIcon name="goals" />
                {t("sidebar.add_goal")}
              </CommandItem>
              <CommandItem onSelect={() => runCommand(() => handleCreateRoutine({}))}>
                <CommonIcon name="routines" />
                {t("sidebar.add_routine")}
              </CommandItem>
            </CommandGroup>
          )}
          <CommandSeparator />
          <CommandGroup heading={t("command_menu.account")}>
            {isAuthenticated ? (
              <>
                <CommandItem
                  value="notification-settings"
                  onSelect={() => {
                    runCommand(() => router.navigate({ to: PATHS.NOTIFICATION_SETTINGS }));
                  }}
                >
                  <Bell />
                  {t("sidebar.notification_settings")}
                </CommandItem>
                <CommandItem
                  value="calendar-settings"
                  onSelect={() => {
                    runCommand(() => router.navigate({ to: PATHS.CALENDAR_SETTINGS }));
                  }}
                >
                  <CalendarCog />
                  {t("sidebar.calendar_settings")}
                </CommandItem>
                <CommandItem
                  value="profile"
                  onSelect={() => {
                    runCommand(() => router.navigate({ to: PATHS.PROFILE }));
                  }}
                >
                  <UserRound />
                  {t("sidebar.profile")}
                </CommandItem>
                <CommandItem
                  value="account"
                  onSelect={() => {
                    runCommand(() => router.navigate({ to: PATHS.ACCOUNT }));
                  }}
                >
                  <SquareUserRound />
                  {t("sidebar.account")}
                </CommandItem>

                <CommandItem onSelect={() => runCommand(() => logout())}>
                  <LogOut />
                  {t("sidebar.log_out")}
                </CommandItem>
              </>
            ) : (
              <>
                <CommandItem onSelect={() => runCommand(() => setLoginDialogOpen(true))}>
                  <UserCheck />
                  {t("button.login")}
                </CommandItem>
                <CommandItem onSelect={() => runCommand(() => setRegisterDialogOpen(true))}>
                  <SquareUserRound />
                  {t("button.register")}
                </CommandItem>
                <CommandItem onSelect={() => runCommand(() => setForgotPasswordDialogOpen(true))}>
                  <SquareAsterisk />
                  {t("auth.forgot_password.title")}
                </CommandItem>
              </>
            )}
          </CommandGroup>
          <CommandSeparator />
          <CommandGroup heading={t("command_menu.theme")}>
            <CommandItem onSelect={() => runCommand(() => setTheme("light"))}>
              <Sun />
              {t("command_menu.light")}
            </CommandItem>
            <CommandItem onSelect={() => runCommand(() => setTheme("dark"))}>
              <Moon />
              {t("command_menu.dark")}
            </CommandItem>
          </CommandGroup>
          <CommandSeparator />
          <CommandGroup heading={t("command_menu.social")}>
            <CommandItem
              onSelect={() => runCommand(() => window.open(WHATSAPP_COMMUNITY_URL, "_blank"))}
            >
              <WhatsAppIcon className="fill-color-foreground dark:fill-white" />
              {t("command_menu.join_whatsapp_community")}
            </CommandItem>
            <CommandItem onSelect={() => runCommand(() => window.open(LINKEDIN_URL, "_blank"))}>
              <LinkedInIcon className="fill-color-foreground dark:fill-white" />
              {t("command_menu.linkedin")}
            </CommandItem>
            <CommandItem onSelect={() => runCommand(() => window.open(X_URL, "_blank"))}>
              <XIcon className="fill-color-foreground dark:fill-white" />
              {t("command_menu.x")}
            </CommandItem>
            <CommandItem onSelect={() => runCommand(() => window.open(INSTAGRAM_URL, "_blank"))}>
              <InstagramIcon className="fill-color-foreground dark:fill-white" />
              {t("command_menu.instagram")}
            </CommandItem>
            <CommandItem onSelect={() => runCommand(() => window.open(TIKTOK_URL, "_blank"))}>
              <TikTokIcon className="fill-color-foreground dark:fill-white" />
              {t("command_menu.tiktok")}
            </CommandItem>
          </CommandGroup>
        </CommandList>
      </CommandDialog>
    </>
  );
}
